import { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import Chip from './KeywordChip';

function AdvancedKeywordBar(props) {
  const scrollContainerRef = useRef(null);
  const [arrowShown, setShowArrow] = useState({ left: false, right: false });
  const [shadeShown, setShowShade] = useState({ left: false, right: false });
  const renderKeywordChips = () => {
    return props.keywords.reduce((acc, field, index) => {
      const { keyword, operator } = field;
      const processedKeyword = keyword
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/g)
        .map((word) => word.trim())
        .filter((word) => word.length > 0)
        .reduce((acc, word, index, arr) => {
          const separatorChip = <Chip key={`separator-${index}`} text="OR" />;
          acc = acc.concat(word);
          acc = index !== arr.length - 1 ? acc.concat(separatorChip) : acc;
          return acc;
        }, []);
      acc = operator
        ? acc.concat(
            <Chip key={`operator-${index}`} type="operator" text={operator} />
          )
        : acc;
      acc = acc.concat(
        <Chip key={`word-${index}`} type="word" text={processedKeyword} />
      );
      return acc;
    }, []);
  };
  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const currentScrollPos = scrollContainer.scrollLeft;
      if (direction === 'left')
        scrollContainer.scrollLeft = currentScrollPos + 20;
      else scrollContainer.scrollLeft = currentScrollPos - 20;
    }
  };
  const showArrowShade = useCallback(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, offsetWidth } = scrollContainer;
      if (scrollLeft > 0) {
        setShowArrow((prev) => ({ ...prev, left: true }));
        setShowShade((prev) => ({ ...prev, left: true }));
        // if scroll maxed out
        if (scrollLeft === scrollWidth - offsetWidth)
          setShowShade((prev) => ({ ...prev, right: false }));
        else setShowShade((prev) => ({ ...prev, right: true }));
      } else {
        setShowArrow((prev) => ({ ...prev, left: false }));
        setShowShade((prev) => ({ ...prev, left: false }));
      }
    }
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      scrollContainer.addEventListener('scroll', showArrowShade);
      return () => {
        scrollContainer.removeEventListener('scroll', showArrowShade);
      };
    }
  }, [showArrowShade, props.isOpen]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const { clientWidth, scrollWidth } = scrollContainer;
      if (clientWidth < scrollWidth) {
        setShowArrow((prev) => ({ ...prev, right: true }));
        setShowShade((prev) => ({ ...prev, right: true }));
      } else {
        setShowArrow((prev) => ({ ...prev, right: false }));
        setShowShade((prev) => ({ ...prev, right: false }));
      }
    }
  }, [props.isOpen]);

  return (
    <div className="flex items-center">
      {arrowShown.left && (
        <button
          onClick={() => handleScroll('right')}
          className="flex justify-center items-center -ml-2 mr-1"
        >
          <Icon
            icon={IconNames.CHEVRON_LEFT}
            iconSize={14}
            className="fill-current text-grey80"
          />
        </button>
      )}
      <div className="h-9 overflow-hidden relative">
        {shadeShown.left && (
          <span className="block h-9 w-5 absolute bg-gradient-to-r from-white"></span>
        )}
        {shadeShown.right && (
          <span className="block h-9 w-5 absolute right-0 bg-gradient-to-l from-white"></span>
        )}
        <div ref={scrollContainerRef} className="h-14 pt-2 overflow-x-scroll">
          {renderKeywordChips()}
        </div>
      </div>
      <div className="flex items-center">
        {arrowShown.right && (
          <button
            onClick={() => handleScroll('left')}
            className="flex justify-center items-center ml-1"
          >
            <Icon
              icon={IconNames.CHEVRON_RIGHT}
              iconSize={14}
              className="fill-current text-grey80 mr-1"
            />
          </button>
        )}
      </div>
    </div>
  );
}

export default AdvancedKeywordBar;
